import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("pe_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("pe_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("pe_user", JSON.stringify(r.data));
    } catch {
      localStorage.removeItem("pe_token");
      localStorage.removeItem("pe_user");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const r = await api.post("/auth/login", { email, password });
      localStorage.setItem("pe_token", r.data.access_token);
      localStorage.setItem("pe_user", JSON.stringify(r.data.user));
      setUser(r.data.user);
      return { ok: true, user: r.data.user };
    } catch (e) {
      return { ok: false, error: apiError(e, "No se pudo iniciar sesión") };
    }
  };

  const logout = () => {
    localStorage.removeItem("pe_token");
    localStorage.removeItem("pe_user");
    // Nota: NO limpiamos pe_quick_device_id — el dispositivo sigue confiado
    // para el siguiente login rápido. Solo se limpia al revocar explícitamente.
    setUser(null);
    window.location.href = "/login";
  };

  const isAdmin = user?.role === "admin";
  const can = (perm) => !!user && (user.role === "admin" || !!user.permissions?.[perm]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, isAdmin, can }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
